/**
 * useMixTemplates Hook
 * Fetches all mix position templates from the API with fallback to static data.
 * Returns { data, loading, error, refetch } where data is MixTemplate[].
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { MixTemplate } from '@/types/production.types';
import { MIX_TEMPLATES } from '@/data/mixPositionTemplates';
import { API_ENDPOINTS } from '@/config/api';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';

interface UseMixTemplatesReturn {
  data: MixTemplate[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useMixTemplates(): UseMixTemplatesReturn {
  const [data, setData] = useState<MixTemplate[]>(MIX_TEMPLATES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithCsrf(`${API_ENDPOINTS.mixTemplates.list}?full=true`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.data && json.data.length > 0) {
        setData(json.data as MixTemplate[]);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch mix templates';
      setError(msg);
      console.error('[useMixTemplates] Fetch failed, using static fallback:', msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      refetch();
    }
  }, [refetch]);

  return { data, loading, error, refetch };
}
